import { FC, FormEvent, useContext, useState } from 'react';
import {
  Alert,
  AlertTitle,
  Button,
  CircularProgress,
  Stack,
  TextField,
} from '@mui/material';
import { EmailOutlined } from '@mui/icons-material';
import * as yup from 'yup';
import { useForm } from 'react-hook-form';
import { useYupValidationResolver } from '@vs-online-only-app/ui';
import { useAuthenticateEmail } from '@vs-online-only-app/state';
import { CreateAccountContext } from '../../CreateAccountContext';

const validationSchema = yup.object({
  email: yup
    .string()
    .required('Required')
    .matches(/^[^\s@]+@[^\s@]+\.[^\s@]+$/, 'Invalid'), // https://github.com/jquense/yup/issues/507
});

export type EmailFormFieldValues = {
  email: string;
};

const EnterEmail: FC = () => {
  const [error, setError] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState(false);
  const { goToNextStep, setEmail, email } = useContext(CreateAccountContext);

  const resolver = useYupValidationResolver(validationSchema);
  const authenticateEmail = useAuthenticateEmail();
  const { handleSubmit, register, formState } = useForm<EmailFormFieldValues>({
    defaultValues: {
      email,
    },
    resolver,
  });

  const { errors } = formState;

  const handleFormSubmit = (e: FormEvent) => {
    e.preventDefault();

    handleSubmit(async (formValues) => {
      const { email } = formValues;

      setIsLoading(true);
      try {
        const { data } = await authenticateEmail.mutateAsync(email);
        if (!data.valid) throw new Error('Invalid email');
        setEmail(email);
        setIsLoading(false);
        goToNextStep();
      } catch (e) {
        setError(true);
      }
    })();
  };

  return (
    <>
      {error && (
        <Alert severity="error" sx={{ width: '-webkit-fill-available' }}>
          <AlertTitle>Invalid email address</AlertTitle>
          The email address you entered doesn't seem to be valid. Please make
          sure it follows the correct format (e.g., example@domain.com) and try
          again.
        </Alert>
      )}
      <form onSubmit={handleFormSubmit} style={{ width: '100%' }}>
        <Stack gap={4}>
          <TextField
            type="email"
            slotProps={{
              input: {
                ...register('email'),
                onChange: (e) => {
                  setError(false);
                  register('email').onChange(e);
                },
                startAdornment: <EmailOutlined sx={{ pr: 1 }} />,
              },
            }}
            autoFocus
            label={`Email address${
              errors?.email?.message ? ` - ${errors.email.message}` : ''
            }`}
            fullWidth
            error={Boolean(errors.email)}
          />
          <Button
            type="submit"
            fullWidth
            size="large"
            variant="contained"
            disabled={isLoading}
          >
            {isLoading ? <CircularProgress size={24} /> : 'Next'}
          </Button>
        </Stack>
      </form>
    </>
  );
};

export default EnterEmail;
